import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Grid, Alert, Radio, CircularProgress, Collapse } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";

import { UnifiedModal } from "../../components/UnifiedModal";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { CustomSelector } from "../../components/CustomSelector";
import { Checkbox } from "../../components/Checkbox";
import { FormField } from "../../components/FormField";
import { Button } from "../../components/Button";
import { Tabs, TabsContent } from "../../components/Tabs";
import { getData } from "../../components/hooks/getData";
import { useAuth } from "../../components/contexts/AuthContext";
import { Avatar, AvatarFallback } from "../../components/Avatar";
import { isExternalAgency as isExternalUserAgency } from "@/utils/agencyFeatures";
import {
  INVALID_URL_MESSAGE,
  SECTOR_OPTIONS,
  SETUP_OPTIONS,
  normalizeUrlWithHttps,
  validateUrlFormat,
} from "./clientFormOptions.js";
import { getPlanLimitError } from "@/utils/planLimits";

const NO_PACK_SELECTOR_VALUE = "__no_pack__";
const PACK_SELECTOR_OPTIONS = [
  { value: NO_PACK_SELECTOR_VALUE, label: "Sin pack" },
  { value: "basic", label: "Pack Basic" },
  { value: "ecommerce", label: "Pack E-commerce" },
  { value: "premium", label: "Pack Premium" },
  { value: "local", label: "Pack Starter" },
  { value: "infoproducto", label: "Pack Infoproducto" },
];

const INITIAL_FORM = {
  nombreEmpresa: "",
  sector: "",
  website: "",
  agenciaAsignada: "",
  packContratado: "",
  useRecommendedSetup: true,
  nombreContacto: "",
  emailContacto: "",
  telefonoContacto: "",
  direccion: "",
  ciudad: "",
  codigoPostal: "",
  notas: "",
  clientifyLink: "",
  razonSocial: "",
  cifNif: "",
  direccionFiscal: "",
  codigoPostalFiscal: "",
  ciudadFiscal: "",
  provinciaFiscal: "",
  consultoresAsignados: [],
};

// Campos que viven dentro de "Datos de contacto": si alguno falla, se despliega la sección.
const EXTRA_FIELDS = [
  "nombreContacto",
  "emailContacto",
  "telefonoContacto",
  "direccion",
  "ciudad",
  "codigoPostal",
  "notas",
];

const FISCAL_FIELDS = [
  "razonSocial",
  "cifNif",
  "direccionFiscal",
  "codigoPostalFiscal",
  "ciudadFiscal",
  "provinciaFiscal",
];

const BACKEND_FIELD_MAP = {
  name: "nombreEmpresa",
  website: "website",
  clientify_url: "clientifyLink",
  agency: "agenciaAsignada",
  contact_email: "emailContacto",
  contact_phone: "telefonoContacto",
  legal_name: "razonSocial",
  tax_id: "cifNif",
  fiscal_address: "direccionFiscal",
  fiscal_postal_code: "codigoPostalFiscal",
  fiscal_city: "ciudadFiscal",
  fiscal_province: "provinciaFiscal",
};

// Orden de foco al mostrar errores (ids de los inputs).
const FOCUSABLE_ERROR_FIELDS = [
  "nombreEmpresa",
  "website",
  "emailContacto",
  "telefonoContacto",
  ...FISCAL_FIELDS,
];

const GENERIC_SAVE_ERROR = "No se ha podido crear el cliente. Inténtalo de nuevo.";

const initialsOf = (label) =>
  (label || "C")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

// Toda la fila es clicable; el contenedor la extiende a sangre.
const CollapsibleSection = ({ id, title, subtitle, open, onToggle, filledCount, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, "&:last-of-type": { borderBottom: "none" } }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: "24px",
          py: 1.75,
          border: "none",
          backgroundColor: "transparent",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
          color: "text.primary",
          transition: "background-color 0.15s",
          "&:hover": { backgroundColor: theme.palette.grey[100] },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "-2px",
          },
        }}
      >
        <ChevronRight
          size={18}
          style={{
            flexShrink: 0,
            color: theme.palette.text.secondary,
            transition: "transform 0.2s ease",
            transform: open ? "rotate(90deg)" : "none",
          }}
        />
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, whiteSpace: "nowrap" }}>{title}</Typography>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "0.875rem",
            color: "text.disabled",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: { xs: "none", sm: "block" },
          }}
        >
          {subtitle}
        </Typography>
        <Typography sx={{ ml: "auto", fontSize: "0.8125rem", color: "text.secondary", whiteSpace: "nowrap" }}>
          {filledCount > 0 ? `${filledCount} ${filledCount === 1 ? "completado" : "completados"}` : "Opcional"}
        </Typography>
      </Box>
      <Collapse in={open} id={id}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            px: "24px",
            pt: 2,
            pb: 2.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

CollapsibleSection.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  filledCount: PropTypes.number,
  children: PropTypes.node,
};

// `required`: no se puede cerrar sin crear el cliente (primer paso de la bienvenida).
export function ClientModal({ isOpen, onClose, onSave, onSuccess, agenciesRefresh, required = false }) {
  const theme = useTheme();
  const { user: currentUser, interceptorReady } = useAuth();
  const role = currentUser?.role;
  const canAssignConsultants = role === "agency_admin" || role === "superadmin";

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [validationErrors, setValidationErrors] = useState({});
  const [emailValid, setEmailValid] = useState(true);
  const [submitError, setSubmitError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [fiscalOpen, setFiscalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState("basicos");

  // Agency admins y consultores crean siempre en su propia agencia; el consultor se autoasigna.
  useEffect(() => {
    if (!currentUser?.agencyId || (role !== "agency_admin" && role !== "consultant")) return;
    setFormData((prev) => ({
      ...prev,
      agenciaAsignada: currentUser.agencyId,
      consultoresAsignados: role === "consultant" ? [currentUser.id] : prev.consultoresAsignados,
    }));
  }, [currentUser, role]);

  const agenciesEndpoint = role === "superadmin" ? "agencies/" : null;
  const { data: agenciesData } = getData(
    agenciesEndpoint,
    interceptorReady && !!agenciesEndpoint,
    [agenciesRefresh]
  );
  const agencies = useMemo(() => (Array.isArray(agenciesData) ? agenciesData : []), [agenciesData]);

  let consultantsEndpoint = null;
  if (role === "agency_admin") {
    consultantsEndpoint = "users/?role=consultant"; // el backend filtra por la agencia del usuario
  } else if (role === "superadmin" && formData.agenciaAsignada) {
    consultantsEndpoint = `users/?role=consultant&agency=${formData.agenciaAsignada}`;
  }
  const consultantsEnabled = interceptorReady && !!consultantsEndpoint;
  const { data: consultantsData, isError: consultantsError } = getData(
    consultantsEndpoint,
    consultantsEnabled
  );
  const consultants = useMemo(() => {
    const list = Array.isArray(consultantsData?.results)
      ? consultantsData.results
      : Array.isArray(consultantsData)
        ? consultantsData
        : [];
    return list.map((consultant) => ({
      value: consultant.id,
      label:
        `${consultant.first_name || ""} ${consultant.last_name || ""}`.trim() ||
        consultant.email ||
        "Consultor",
      email: consultant.email,
      profile_image: consultant.profile_image,
    }));
  }, [consultantsData]);

  // Hasta saber si la agencia tiene consultores no pintamos la estructura del modal,
  // para que el stepper no aparezca y desaparezca. Solo afecta a la carga inicial del
  // agency admin; el superadmin depende de la agencia que elija en el formulario.
  const isResolvingLayout =
    role === "agency_admin" && consultantsEnabled && consultantsData === undefined && !consultantsError;

  const selectedAgency = agencies.find(
    (agency) => String(agency.id) === String(formData.agenciaAsignada)
  );
  const isExternalAgency =
    role === "superadmin" ? selectedAgency?.agency_type === "external" : isExternalUserAgency(currentUser);

  const hasAssignmentStep = canAssignConsultants && consultants.length > 0;
  const steps = [
    { value: "basicos", label: "Datos básicos" },
    ...(hasAssignmentStep ? [{ value: "asignacion", label: "Asignación" }] : []),
    ...(isExternalAgency ? [{ value: "setup", label: "Setup" }] : []),
  ];
  const isWizard = steps.length > 1;
  const currentStep = steps.some((step) => step.value === activeStep) ? activeStep : "basicos";
  const currentStepIndex = steps.findIndex((step) => step.value === currentStep);
  const isLastStep = currentStepIndex === steps.length - 1;

  const countFilled = (fields) => fields.filter((field) => String(formData[field] || "").trim()).length;
  const extraFilledCount = countFilled(EXTRA_FIELDS);
  const fiscalFilledCount = countFilled(FISCAL_FIELDS);

  // Pack solo para agencias SERSEO (no externas); el superadmin lo ve al elegir una.
  const showPackField = role === "superadmin" ? !!selectedAgency && !isExternalAgency : !isExternalAgency;

  const clearFieldError = (field) => {
    setSubmitError(null);
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleInputChange = (field, value) => {
    clearFieldError(field);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAgencyChange = (value) => {
    clearFieldError("agenciaAsignada");
    setFormData((prev) => ({ ...prev, agenciaAsignada: value, consultoresAsignados: [], packContratado: "" }));
  };

  const toggleConsultant = (consultantId) => {
    setFormData((prev) => {
      const selected = prev.consultoresAsignados.includes(consultantId);
      return {
        ...prev,
        consultoresAsignados: selected
          ? prev.consultoresAsignados.filter((id) => id !== consultantId)
          : [...prev.consultoresAsignados, consultantId],
      };
    });
  };

  const collectErrors = () => {
    const errors = {};
    if (!formData.nombreEmpresa.trim()) {
      errors.nombreEmpresa = "Indica el nombre de la empresa";
    }
    if (!formData.agenciaAsignada) {
      errors.agenciaAsignada = "Selecciona una agencia";
    }
    const websiteError = validateUrlFormat(formData.website);
    if (websiteError) errors.website = websiteError;
    const clientifyError = validateUrlFormat(formData.clientifyLink);
    if (clientifyError) errors.clientifyLink = clientifyError;
    if (formData.emailContacto.trim() && (!formData.emailContacto.includes("@") || !emailValid)) {
      errors.emailContacto = "Introduce un correo electrónico válido";
    }
    if (formData.telefonoContacto.length > 20) {
      errors.telefonoContacto = "Máximo 20 caracteres";
    }
    return errors;
  };

  // Todos los campos validables viven en el primer paso: si hay errores, volvemos a él.
  const showErrors = (errors) => {
    setValidationErrors(errors);
    setActiveStep("basicos");
    if (EXTRA_FIELDS.some((field) => errors[field])) setMoreOpen(true);
    if (FISCAL_FIELDS.some((field) => errors[field])) setFiscalOpen(true);
    if (errors.agenciaAsignada && role !== "superadmin") {
      setSubmitError("No se ha podido determinar tu agencia. Recarga la página e inténtalo de nuevo.");
    }
    const firstField = FOCUSABLE_ERROR_FIELDS.find((field) => errors[field]);
    if (firstField) {
      setTimeout(() => {
        const input = document.getElementById(firstField);
        input?.focus({ preventScroll: true });
        input?.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 250);
    }
  };

  const goToStep = (nextStep) => {
    const nextIndex = steps.findIndex((step) => step.value === nextStep);
    if (currentStep === "basicos" && nextIndex > 0) {
      const errors = collectErrors();
      if (Object.keys(errors).length > 0) {
        showErrors(errors);
        return;
      }
    }
    setActiveStep(nextStep);
  };

  const buildPayload = () => {
    const payload = {
      name: formData.nombreEmpresa.trim(),
      website: normalizeUrlWithHttps(formData.website),
      clientify_url: normalizeUrlWithHttps(formData.clientifyLink),
      agency: parseInt(formData.agenciaAsignada, 10),
      sector: formData.sector || "",
      contact_name: formData.nombreContacto || "",
      contact_email: formData.emailContacto || "",
      contact_phone: formData.telefonoContacto || "",
      address: formData.direccion || "",
      location: formData.ciudad || "",
      notes: formData.notas || "",
      onboarding_status: "pending",
      contract_start_date: new Date().toISOString().split("T")[0],
      // Sin datos fiscales explícitos se mantienen los valores de antes (nombre, CP y ciudad).
      legal_name: formData.razonSocial.trim(),
      tax_id: formData.cifNif.trim(),
      fiscal_address: formData.direccionFiscal.trim(),
      fiscal_postal_code: formData.codigoPostalFiscal.trim(),
      fiscal_city: formData.ciudadFiscal.trim(),
      fiscal_province: formData.provinciaFiscal.trim(),
      consultoresAsignados: formData.consultoresAsignados || [],
    };
    if (isExternalAgency) {
      payload.setup_not_needed = !formData.useRecommendedSetup;
    } else if (formData.packContratado) {
      payload.setup_variant = formData.packContratado;
    }
    return payload;
  };

  const handleSave = async () => {
    const errors = collectErrors();
    if (Object.keys(errors).length > 0) {
      showErrors(errors);
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    try {
      await onSave?.(buildPayload());
      onSuccess?.("Cliente creado exitosamente");
      onClose();
    } catch (error) {
      // El límite de plan ya lo muestra Clients.jsx con su propio diálogo
      if (getPlanLimitError(error)) return;

      const backendErrors = error?.response?.data;
      const fieldErrors = {};
      if (backendErrors && typeof backendErrors === "object") {
        Object.entries(BACKEND_FIELD_MAP).forEach(([apiField, formField]) => {
          const raw = backendErrors[apiField];
          if (!raw) return;
          const message = Array.isArray(raw) ? raw[0] : String(raw);
          fieldErrors[formField] =
            formField === "website" || formField === "clientifyLink" ? INVALID_URL_MESSAGE : message;
        });
      }
      // unique_together (name, agency) llega como non_field_errors, en inglés y sin campo.
      const nonFieldMessage = [].concat(backendErrors?.non_field_errors ?? [])[0];
      if (typeof nonFieldMessage === "string" && /must make a unique set/i.test(nonFieldMessage)) {
        fieldErrors.nombreEmpresa = "Ya existe un cliente con este nombre en la agencia.";
      }
      if (Object.keys(fieldErrors).length > 0) {
        showErrors(fieldErrors);
      } else {
        const firstBackendMessage =
          backendErrors && typeof backendErrors === "object"
            ? [].concat(Object.values(backendErrors)[0] ?? [])[0]
            : null;
        setSubmitError(typeof firstBackendMessage === "string" ? firstBackendMessage : GENERIC_SAVE_ERROR);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Los errores de campo se muestran junto a cada input; aquí solo los que no tienen campo.
  const statusAlert = submitError && (
    <Alert severity="error" sx={{ fontSize: "0.875rem", borderRadius: "10px" }}>
      {submitError}
    </Alert>
  );

  const footerHint = (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", color: "text.secondary", minWidth: 0 }}>
      <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
      <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.45 }}>
        Activos e integraciones se completan desde la ficha.
      </Typography>
    </Box>
  );

  const moreDataSection = (
    <CollapsibleSection
      id="client-modal-more-data"
      title="Datos de contacto"
      subtitle="Persona de contacto, dirección, notas"
      open={moreOpen}
      onToggle={() => setMoreOpen((open) => !open)}
      filledCount={extraFilledCount}
    >
      <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormField label="Persona de contacto" htmlFor="nombreContacto">
              <CustomTextField
                id="nombreContacto"
                value={formData.nombreContacto}
                onChange={(e) => handleInputChange("nombreContacto", e.target.value)}
                placeholder="Ej.: Juan Pérez"
              />
            </FormField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormField label="Email" htmlFor="emailContacto">
              <CustomTextField
                id="emailContacto"
                type="email"
                value={formData.emailContacto}
                onChange={(e) => handleInputChange("emailContacto", e.target.value)}
                onValidationChange={setEmailValid}
                placeholder="juan@empresa.com"
                error={!!validationErrors.emailContacto}
                errorMessage={validationErrors.emailContacto}
              />
            </FormField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormField label="Teléfono" htmlFor="telefonoContacto">
              <CustomTextField
                id="telefonoContacto"
                type="tel"
                value={formData.telefonoContacto}
                onChange={(e) => handleInputChange("telefonoContacto", e.target.value)}
                placeholder="+34 600 000 000"
                maxLength={20}
                error={!!validationErrors.telefonoContacto}
                errorMessage={validationErrors.telefonoContacto}
              />
            </FormField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormField label="Dirección" htmlFor="direccion">
              <CustomTextField
                id="direccion"
                value={formData.direccion}
                onChange={(e) => handleInputChange("direccion", e.target.value)}
                placeholder="Calle, número, piso…"
              />
            </FormField>
          </Grid>
          <Grid size={{ xs: 8, sm: 3 }}>
            <FormField label="Ciudad" htmlFor="ciudad">
              <CustomTextField
                id="ciudad"
                value={formData.ciudad}
                onChange={(e) => handleInputChange("ciudad", e.target.value)}
                placeholder="Ej.: Madrid"
              />
            </FormField>
          </Grid>
          <Grid size={{ xs: 4, sm: 3 }}>
            <FormField label="CP" htmlFor="codigoPostal">
              <CustomTextField
                id="codigoPostal"
                value={formData.codigoPostal}
                onChange={(e) => handleInputChange("codigoPostal", e.target.value)}
                placeholder="28001"
              />
            </FormField>
          </Grid>
      </Grid>

      <FormField label="Notas" htmlFor="notas">
        <CustomTextArea
          id="notas"
          rows={2}
          value={formData.notas}
          onChange={(e) => handleInputChange("notas", e.target.value)}
          placeholder="Cualquier detalle útil sobre el cliente…"
          setValueOnClickAway={true}
        />
      </FormField>
    </CollapsibleSection>
  );

  const fiscalSection = (
    <CollapsibleSection
      id="client-modal-fiscal-data"
      title="Datos fiscales"
      subtitle="Razón social, CIF/NIF, dirección fiscal"
      open={fiscalOpen}
      onToggle={() => setFiscalOpen((open) => !open)}
      filledCount={fiscalFilledCount}
    >
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 5 }}>
          <FormField label="Razón social" htmlFor="razonSocial">
            <CustomTextField
              id="razonSocial"
              value={formData.razonSocial}
              onChange={(e) => handleInputChange("razonSocial", e.target.value)}
              placeholder="Ej.: La Buena Mesa S.L."
              maxLength={200}
              error={!!validationErrors.razonSocial}
              errorMessage={validationErrors.razonSocial}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormField label="CIF/NIF" htmlFor="cifNif">
            <CustomTextField
              id="cifNif"
              value={formData.cifNif}
              onChange={(e) => handleInputChange("cifNif", e.target.value)}
              placeholder="B12345678"
              maxLength={50}
              error={!!validationErrors.cifNif}
              errorMessage={validationErrors.cifNif}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormField label="Provincia" htmlFor="provinciaFiscal">
            <CustomTextField
              id="provinciaFiscal"
              value={formData.provinciaFiscal}
              onChange={(e) => handleInputChange("provinciaFiscal", e.target.value)}
              placeholder="Ej.: Madrid"
              maxLength={100}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormField label="Dirección fiscal" htmlFor="direccionFiscal">
            <CustomTextField
              id="direccionFiscal"
              value={formData.direccionFiscal}
              onChange={(e) => handleInputChange("direccionFiscal", e.target.value)}
              placeholder="Calle, número, piso…"
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 4, sm: 2 }}>
          <FormField label="CP" htmlFor="codigoPostalFiscal">
            <CustomTextField
              id="codigoPostalFiscal"
              value={formData.codigoPostalFiscal}
              onChange={(e) => handleInputChange("codigoPostalFiscal", e.target.value)}
              placeholder="28001"
              maxLength={20}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 8, sm: 4 }}>
          <FormField label="Ciudad" htmlFor="ciudadFiscal">
            <CustomTextField
              id="ciudadFiscal"
              value={formData.ciudadFiscal}
              onChange={(e) => handleInputChange("ciudadFiscal", e.target.value)}
              placeholder="Ej.: Madrid"
              maxLength={100}
            />
          </FormField>
        </Grid>
      </Grid>
    </CollapsibleSection>
  );

  const basicStep = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <FormField label="Nombre de la empresa" htmlFor="nombreEmpresa" required>
        <CustomTextField
          id="nombreEmpresa"
          autoFocus
          value={formData.nombreEmpresa}
          onChange={(e) => handleInputChange("nombreEmpresa", e.target.value)}
          placeholder="Ej.: Restaurante La Buena Mesa"
          error={!!validationErrors.nombreEmpresa}
          errorMessage={validationErrors.nombreEmpresa}
        />
      </FormField>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormField label="Sector" htmlFor="sector">
            <CustomSelector
              value={formData.sector}
              onChange={(value) => handleInputChange("sector", value)}
              options={SECTOR_OPTIONS}
              placeholder="Elige o escribe un sector"
              searchable
              freeSolo
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormField label="Página web" htmlFor="website">
            <CustomTextField
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange("website", e.target.value)}
              placeholder="miweb.com"
              maxLength={200}
              validateUrl={true}
              error={!!validationErrors.website}
              errorMessage={validationErrors.website}
            />
          </FormField>
        </Grid>
      </Grid>

      {role === "superadmin" && (
        <FormField label="Agencia" htmlFor="agenciaAsignada" required error={validationErrors.agenciaAsignada}>
          <CustomSelector
            value={formData.agenciaAsignada}
            onChange={handleAgencyChange}
            options={agencies.map((agency) => ({ value: agency.id, label: agency.name }))}
            placeholder="Selecciona una agencia"
            searchable
          />
        </FormField>
      )}

      {showPackField && (
        <FormField label="Pack contratado" htmlFor="packContratado">
          <CustomSelector
            value={formData.packContratado}
            onChange={(value) =>
              handleInputChange("packContratado", value === NO_PACK_SELECTOR_VALUE ? "" : value)
            }
            options={PACK_SELECTOR_OPTIONS}
            placeholder="Sin pack"
          />
        </FormField>
      )}

      {/* A sangre: anula el padding de 24px del contenido de UnifiedModal */}
      <Box sx={{ mt: 0.5, mx: "-24px", mb: "-24px", borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
        {moreDataSection}
        {fiscalSection}
      </Box>
    </Box>
  );

  const assignmentStep = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>¿Quién llevará este cliente?</Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.25 }}>
          Opcional. Si no eliges a nadie, quedará sin asignar hasta que lo hagas desde su ficha.
        </Typography>
      </Box>
      <Box
        role="group"
        aria-label="Consultores"
        sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 320, overflowY: "auto", pr: 0.5 }}
      >
        {consultants.map((consultant) => {
          const selected = formData.consultoresAsignados.includes(consultant.value);
          return (
            <Box
              key={consultant.value}
              role="checkbox"
              aria-checked={selected}
              tabIndex={0}
              onClick={() => toggleConsultant(consultant.value)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  toggleConsultant(consultant.value);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: "10px",
                cursor: "pointer",
                border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                "&:hover": { backgroundColor: selected ? theme.palette.background.paper : theme.palette.action.hover },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: "1px",
                },
              }}
            >
              <Checkbox checked={selected} onChange={() => {}} tabIndex={-1} />
              <Avatar
                src={consultant.profile_image || undefined}
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.text.secondary,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <AvatarFallback
                  sx={{
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  {initialsOf(consultant.label)}
                </AvatarFallback>
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                  {consultant.label}
                </Typography>
                {consultant.email && (
                  <Typography noWrap sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    {consultant.email}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const setupStep = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
          ¿Queréis usar el Setup recomendado?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          El Setup es la puesta en marcha del cliente: análisis, web, SEO, redes sociales y campañas
          organizadas por fases. Tanto vosotros como el cliente podréis seguir su progreso, y las fases
          que no apliquen se pueden marcar como no necesarias.
        </Typography>
      </Box>

      <Box role="radiogroup" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {SETUP_OPTIONS.map((option) => {
          const selected = formData.useRecommendedSetup === option.value;
          return (
            <Box
              key={option.title}
              onClick={() => handleInputChange("useRecommendedSetup", option.value)}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
                p: 1,
                pr: 2,
                cursor: "pointer",
                borderRadius: "10px",
                border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                "&:hover": { backgroundColor: selected ? theme.palette.background.paper : theme.palette.action.hover },
              }}
            >
              <Radio size="small" checked={selected} />
              <Box sx={{ pt: "6px" }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{option.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const stepContent = {
    basicos: basicStep,
    asignacion: assignmentStep,
    setup: setupStep,
  };

  const submitButton = (
    <Button onClick={handleSave} disabled={isSaving || isResolvingLayout}>
      {isSaving && <CircularProgress size={14} sx={{ color: "inherit" }} />}
      {isSaving ? "Creando…" : "Crear cliente"}
    </Button>
  );

  const footer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: { xs: 1.5, sm: 2 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{footerHint}</Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          justifyContent: "flex-end",
          flexShrink: 0,
          "& > button": { flex: { xs: 1, sm: "none" } },
        }}
      >
        {isWizard && currentStepIndex > 0 ? (
          <Button
            variant="outline"
            onClick={() => setActiveStep(steps[currentStepIndex - 1].value)}
            disabled={isSaving}
          >
            <ChevronLeft size={16} />
            Anterior
          </Button>
        ) : (
          !required && (
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancelar
            </Button>
          )
        )}
        {isWizard && !isLastStep ? (
          <Button onClick={() => goToStep(steps[currentStepIndex + 1].value)}>
            Siguiente
            <ChevronRight size={16} />
          </Button>
        ) : (
          submitButton
        )}
      </Box>
    </Box>
  );

  const renderStepBody = (value) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {statusAlert}
      {stepContent[value]}
    </Box>
  );

  return (
    <UnifiedModal
      open={isOpen}
      onClose={required ? () => {} : onClose}
      hideCloseButton={required}
      title={required ? "Crea tu primer cliente" : "Nuevo cliente"}
      maxWidth="sm"
      footer={footer}
      contentSx={isWizard ? { minHeight: 400 } : undefined}
    >
      {isResolvingLayout ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isWizard ? (
        <Tabs
          tabs={steps}
          value={currentStep}
          onValueChange={goToStep}
          contentSx={{ overflow: "visible", mb: 0 }}
        >
          {steps.map((step) => (
            <TabsContent key={step.value} value={step.value}>
              {renderStepBody(step.value)}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        renderStepBody("basicos")
      )}
    </UnifiedModal>
  );
}

ClientModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onSuccess: PropTypes.func,
  agenciesRefresh: PropTypes.any,
  required: PropTypes.bool,
};
